// MT-051 — visor del registro de accesos del HCE: filtros EN EL SERVIDOR (usuario, paciente, acción, resultado, fechas), paginación y exportación CSV/PDF con hash.
// Lo usa el admin del HCE (identidades) y el Modo Auditor (solo lectura: los pacientes llegan seudonimizados y no hay filtro por paciente).
import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  consultarAccesosHce,
  exportarAccesosHce,
  sha256DeArchivo,
  verificarExportacionAccesos,
  type FiltrosAccesos,
  type PaginaAccesos,
} from '@/lib/hceAuditoria';

const ACCIONES_FILTRO = ['hce-record-read', 'hce-record-write', 'hce-questionnaire-submit', 'hce-escritura-bloqueada', 'hce-acceso-denegado', 'hce-acceso-emergencia', 'hce-acceso-emergencia-revision', 'hce-dato-revelado', 'hce-record-copy'] as const;
const POR_PAGINA = 50;
const VACIOS = { usuario: '', paciente: '', accion: '', resultado: '', desde: '', hasta: '' };

type Aviso = { tipo: 'ok' | 'error'; texto: string };

export function VisorAccesos({ token, esAuditor = false }: { token: string; esAuditor?: boolean }): JSX.Element {
  const { t } = useTranslation();
  const [form, setForm] = useState(VACIOS);
  const [aplicados, setAplicados] = useState(VACIOS);
  const [pagina, setPagina] = useState(1);
  const [datos, setDatos] = useState<PaginaAccesos | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exportando, setExportando] = useState<'csv' | 'pdf' | null>(null);
  const [aviso, setAviso] = useState<Aviso | null>(null);

  // Memoizado: la identidad del objeto es la dependencia del efecto de carga (un objeto nuevo en cada render lo dispararía sin fin).
  const filtros = useMemo((): FiltrosAccesos => {
    const f: FiltrosAccesos = {};
    if (aplicados.usuario.trim()) f.usuario = aplicados.usuario.trim();
    if (!esAuditor && aplicados.paciente.trim()) f.paciente = aplicados.paciente.trim();
    if (aplicados.accion) f.accion = aplicados.accion;
    if (aplicados.resultado === 'ok' || aplicados.resultado === 'denegado' || aplicados.resultado === 'error') f.resultado = aplicados.resultado;
    if (aplicados.desde) f.desde = aplicados.desde;
    if (aplicados.hasta) f.hasta = aplicados.hasta;
    return f;
  }, [aplicados, esAuditor]);

  useEffect(() => {
    let vigente = true;
    setCargando(true);
    setError(null);
    consultarAccesosHce(token, filtros, pagina, POR_PAGINA)
      .then((d) => {
        if (vigente) setDatos(d);
      })
      .catch((e: unknown) => {
        if (vigente) setError(e instanceof Error ? e.message : t('auditoria.visor.errorCargar'));
      })
      .finally(() => {
        if (vigente) setCargando(false);
      });
    return () => {
      vigente = false;
    };
  }, [token, filtros, pagina, t]);

  async function exportar(formato: 'csv' | 'pdf'): Promise<void> {
    setExportando(formato);
    setAviso(null);
    try {
      const r = await exportarAccesosHce(token, formato, filtros);
      const resumen = t('auditoria.visor.exportada', { nombre: r.nombre, filas: r.filas, constancia: r.constancia ?? '—', cadena: r.cadenaOk ? t('auditoria.visor.cadenaOk') : t('auditoria.visor.cadenaRota') });
      setAviso({ tipo: 'ok', texto: `${resumen} SHA-256: ${r.sha256}` });
    } catch (e) {
      setAviso({ tipo: 'error', texto: e instanceof Error ? e.message : String(e) });
    }
    setExportando(null);
  }

  async function verificarArchivo(archivo: File | undefined): Promise<void> {
    if (!archivo) return;
    setAviso(null);
    try {
      const r = await verificarExportacionAccesos(token, await sha256DeArchivo(archivo));
      setAviso(
        r.encontrada
          ? { tipo: 'ok', texto: t('auditoria.visor.verificadoOk', { formato: (r.formato ?? '').toUpperCase(), filas: r.filas ?? 0, cuando: new Date(r.cuando ?? '').toLocaleString('es-EC'), seq: r.seq ?? '—' }) }
          : { tipo: 'error', texto: t('auditoria.visor.verificadoNo') },
      );
    } catch (e) {
      setAviso({ tipo: 'error', texto: e instanceof Error ? e.message : String(e) });
    }
  }

  const campo = 'w-full rounded-md border border-border bg-hce-surface px-3 py-2 text-sm text-hce-ink';

  return (
    <section className="space-y-3" aria-label={t('auditoria.visor.titulo')}>
      <div>
        <h2 className="text-sm font-medium text-hce-ink">{t('auditoria.visor.titulo')}</h2>
        <p className="text-xs text-hce-muted mt-0.5">{t(esAuditor ? 'auditoria.visor.descripcionAuditor' : 'auditoria.visor.descripcion')}</p>
      </div>

      <form
        className="grid gap-2 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          setPagina(1);
          setAplicados(form);
        }}
      >
        <label className="text-xs text-hce-muted space-y-1">
          {t(esAuditor ? 'auditoria.visor.usuarioAuditor' : 'auditoria.visor.usuario')}
          <Input value={form.usuario} maxLength={80} placeholder={esAuditor ? 'U-…' : undefined} onChange={(e) => setForm({ ...form, usuario: e.target.value })} />
        </label>
        {!esAuditor && (
          <label className="text-xs text-hce-muted space-y-1">
            {t('auditoria.visor.paciente')}
            <Input value={form.paciente} maxLength={80} onChange={(e) => setForm({ ...form, paciente: e.target.value })} />
          </label>
        )}
        <label className="text-xs text-hce-muted space-y-1">
          {t('auditoria.visor.accion')}
          <select className={campo} value={form.accion} onChange={(e) => setForm({ ...form, accion: e.target.value })}>
            <option value="">{t('auditoria.visor.todasAcciones')}</option>
            {ACCIONES_FILTRO.map((a) => (
              <option key={a} value={a}>
                {t(`auditoria.visor.acciones.${a}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-hce-muted space-y-1">
          {t('auditoria.visor.resultado')}
          <select className={campo} value={form.resultado} onChange={(e) => setForm({ ...form, resultado: e.target.value })}>
            <option value="">{t('auditoria.visor.todosResultados')}</option>
            <option value="ok">{t('auditoria.visor.permitido')}</option>
            <option value="denegado">{t('auditoria.visor.denegado')}</option>
            <option value="error">{t('auditoria.visor.conError')}</option>
          </select>
        </label>
        <label className="text-xs text-hce-muted space-y-1">
          {t('auditoria.visor.desde')}
          <Input type="date" value={form.desde} onChange={(e) => setForm({ ...form, desde: e.target.value })} />
        </label>
        <label className="text-xs text-hce-muted space-y-1">
          {t('auditoria.visor.hasta')}
          <Input type="date" value={form.hasta} onChange={(e) => setForm({ ...form, hasta: e.target.value })} />
        </label>
        <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
          <Button type="submit" size="sm">
            {t('auditoria.visor.buscar')}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setForm(VACIOS);
              setAplicados(VACIOS);
              setPagina(1);
            }}
          >
            {t('auditoria.visor.limpiar')}
          </Button>
          {datos && <span className="ml-auto text-xs text-hce-muted">{t('auditoria.visor.total', { total: datos.total })}</span>}
        </div>
      </form>

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" disabled={exportando !== null} onClick={() => void exportar('csv')}>
          {exportando === 'csv' ? t('auditoria.visor.exportando') : t('auditoria.visor.exportarCsv')}
        </Button>
        <Button variant="outline" size="sm" disabled={exportando !== null} onClick={() => void exportar('pdf')}>
          {exportando === 'pdf' ? t('auditoria.visor.exportando') : t('auditoria.visor.exportarPdf')}
        </Button>
        <label className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs text-hce-ink cursor-pointer">
          {t('auditoria.visor.verificarArchivo')}
          <input type="file" aria-label={t('auditoria.visor.verificarArchivo')} className="sr-only" accept=".csv,.pdf" onChange={(e) => void verificarArchivo(e.target.files?.[0])} />
        </label>
      </div>

      {aviso && (
        <Alert variant={aviso.tipo === 'error' ? 'red' : undefined}>
          <AlertDescription className="break-all">{aviso.texto}</AlertDescription>
        </Alert>
      )}
      {error && (
        <Alert variant="red">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {cargando && !datos ? (
        <p className="text-sm text-hce-muted">{t('auditoria.visor.cargando')}</p>
      ) : datos && datos.filas.length === 0 ? (
        <p className="text-sm text-hce-muted">{t('auditoria.visor.sinResultados')}</p>
      ) : datos ? (
        <div className={cargando ? 'opacity-60' : ''}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('auditoria.visor.columnas.cuando')}</TableHead>
                <TableHead>{t('auditoria.visor.columnas.usuario')}</TableHead>
                <TableHead>{t('auditoria.visor.columnas.accion')}</TableHead>
                <TableHead>{t('auditoria.visor.columnas.paciente')}</TableHead>
                <TableHead>{t('auditoria.visor.columnas.recurso')}</TableHead>
                <TableHead>{t('auditoria.visor.columnas.modulo')}</TableHead>
                <TableHead>{t('auditoria.visor.columnas.ip')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {datos.filas.map((f, i) => (
                <TableRow key={`${f.seq ?? 'x'}-${i}`}>
                  <TableCell className="text-xs text-hce-muted whitespace-nowrap">{new Date(f.cuando).toLocaleString('es-EC')}</TableCell>
                  <TableCell>{f.usuario ?? '—'}</TableCell>
                  <TableCell>
                    {t(`auditoria.visor.acciones.${f.accion}`, { defaultValue: f.accion })}
                    {f.resultado === 'denegado' && (
                      <>
                        {' '}
                        <Badge variant="clay">{t('auditoria.visor.denegado')}</Badge>
                      </>
                    )}
                    {f.resultado === 'error' && (
                      <>
                        {' '}
                        <Badge variant="clay">{t('auditoria.visor.conError')}</Badge>
                      </>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{f.paciente ?? '—'}</TableCell>
                  <TableCell className="font-mono text-xs">{f.recurso ?? '—'}</TableCell>
                  <TableCell className="text-xs">{f.modulo && f.modulo !== 'desconocido' ? f.modulo : '—'}</TableCell>
                  <TableCell className="font-mono text-xs">{f.ip ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <nav className="mt-2 flex items-center justify-between gap-3 text-xs text-hce-muted" aria-label={t('auditoria.visor.titulo')}>
            <Button variant="outline" size="sm" disabled={pagina <= 1 || cargando} onClick={() => setPagina(pagina - 1)}>
              {t('auditoria.visor.anterior')}
            </Button>
            <span>{t('auditoria.visor.pagina', { pagina: datos.pagina, paginas: datos.paginas })}</span>
            <Button variant="outline" size="sm" disabled={pagina >= datos.paginas || cargando} onClick={() => setPagina(pagina + 1)}>
              {t('auditoria.visor.siguiente')}
            </Button>
          </nav>
        </div>
      ) : null}
    </section>
  );
}
